/**
 * SystemTray — Sidebar section [6]: footer with display mode + language.
 *
 * Compact, low-priority controls that should NOT dominate the sidebar
 * height (otherwise the FilterPanel gets squeezed). All labels go through
 * i18n; the display toggle text is computed in JS so it works for any locale.
 *
 * The optional palette switcher is hidden when `availablePalettes` is empty
 * (the case for letteratura — single fixed palette).
 */

import { BaseSection, type SectionId } from '@/core/BaseSection';
import type { Locale } from '@/bridge/bridge-types';
import { icon } from '@/core/icons';

type Palette = 'frost' | 'copper' | 'emerald' | 'lavender';
type DisplayMode = 'desktop' | 'lim';

export interface SystemTrayOptions {
  supportsThemeToggle?: boolean;
  availableLocales?: Locale[];
  availablePalettes?: Palette[];
  defaultPalette?: Palette;
  defaultDisplayMode?: DisplayMode;
}

export class SystemTray extends BaseSection {
  readonly sectionId: SectionId = 'tray';
  readonly sectionTitle = 'System Tray';

  private readonly options: SystemTrayOptions;
  private currentPalette: Palette | null;
  private currentDisplay: DisplayMode;

  private localeSelect!: HTMLSelectElement;
  private displayBtn!: HTMLButtonElement;
  private langLabelEl!: HTMLElement;

  constructor(
    element: HTMLElement,
    context: ConstructorParameters<typeof BaseSection>[1],
    options: SystemTrayOptions = {},
  ) {
    super(element, context);
    this.options = {
      supportsThemeToggle: options.supportsThemeToggle ?? false,
      availableLocales: options.availableLocales ?? [...context.i18n.getAvailableLocales()],
      availablePalettes: options.availablePalettes ?? [],
      defaultPalette: options.defaultPalette,
      defaultDisplayMode: options.defaultDisplayMode ?? 'desktop',
    };
    this.currentPalette = this.options.defaultPalette ?? null;
    // D7: ricorda l'ultima vista Testo/Mappa scelta (persistita tra i reload).
    this.currentDisplay = SystemTray.readStoredDisplay() ?? this.options.defaultDisplayMode!;
  }

  private static readonly DISPLAY_KEY = 'edutool:display-mode';
  private static readStoredDisplay(): DisplayMode | null {
    try {
      const v = localStorage.getItem(SystemTray.DISPLAY_KEY);
      return v === 'lim' || v === 'desktop' ? v : null;
    } catch { return null; }
  }
  private static persistDisplay(mode: DisplayMode): void {
    try { localStorage.setItem(SystemTray.DISPLAY_KEY, mode); } catch { /* storage non disponibile */ }
  }

  protected async onInit(): Promise<void> {
    const palettes = this.options.availablePalettes ?? [];
    const showPalettes = palettes.length > 1;

    this.element.innerHTML = `
      <div class="system-tray">
        <div class="tray-row tray-display-row">
          <button type="button" class="tray-display-toggle" data-display-toggle aria-live="polite"></button>
        </div>
        <div class="tray-row">
          <label class="tray-label" for="tray-locale-select"></label>
          <select id="tray-locale-select" class="tray-select">
            ${this.options.availableLocales!.map(
              (l) => `<option value="${l}">${l.toUpperCase()}</option>`,
            ).join('')}
          </select>
        </div>
        ${showPalettes ? `
          <div class="tray-row tray-palette-row">
            <span class="tray-label">Palette</span>
            <div class="tray-palette-btns">
              ${palettes.map((p) => `<button type="button" class="tray-palette-dot${p === this.currentPalette ? ' is-active' : ''}" data-palette="${p}" title="${p}" aria-label="Palette ${p}"><span class="tray-dot tray-dot--${p}"></span></button>`).join('')}
            </div>
          </div>
        ` : ''}
      </div>
    `;

    this.localeSelect = this.element.querySelector<HTMLSelectElement>('#tray-locale-select')!;
    this.displayBtn = this.element.querySelector<HTMLButtonElement>('[data-display-toggle]')!;
    this.langLabelEl = this.element.querySelector<HTMLElement>('.tray-label')!;

    // Initial state
    this.localeSelect.value = this.context.locale;
    document.documentElement.setAttribute('data-display', this.currentDisplay);
    if (this.currentPalette) document.documentElement.setAttribute('data-palette', this.currentPalette);
    this.renderLabels();

    this.addEventListener(this.localeSelect, 'change', () => {
      const locale = this.localeSelect.value as Locale;
      this.context.bus.emit<{ locale: Locale }>('locale:change', { locale });
    });

    this.addEventListener(this.displayBtn, 'click', () => {
      this.currentDisplay = this.currentDisplay === 'desktop' ? 'lim' : 'desktop';
      document.documentElement.setAttribute('data-display', this.currentDisplay);
      SystemTray.persistDisplay(this.currentDisplay);
      this.renderLabels();
      this.context.bus.emit('display:change', { mode: this.currentDisplay });
    });

    this.addCleanup(
      this.context.bus.on<{ mode: DisplayMode }>('set_display_mode', ({ mode }) => {
        this.currentDisplay = mode;
        document.documentElement.setAttribute('data-display', mode);
        SystemTray.persistDisplay(mode);
        this.renderLabels();
      }),
    );

    if (showPalettes) {
      const paletteBtns = this.element.querySelectorAll<HTMLButtonElement>('.tray-palette-dot');
      paletteBtns.forEach((btn) => {
        this.addEventListener(btn, 'click', () => {
          const palette = btn.dataset.palette as Palette;
          this.currentPalette = palette;
          document.documentElement.setAttribute('data-palette', palette);
          paletteBtns.forEach((b) => b.classList.toggle('is-active', b.dataset.palette === palette));
        });
      });
    }
  }

  protected onLocaleChange(): void {
    if (this.localeSelect) this.localeSelect.value = this.context.locale;
    this.renderLabels();
  }

  private renderLabels(): void {
    if (this.langLabelEl) this.langLabelEl.textContent = this.context.i18n.t('sidebar.settings.language');
    if (this.displayBtn) {
      const dest: DisplayMode = this.currentDisplay === 'desktop' ? 'lim' : 'desktop';
      const glyph = dest === 'lim' ? icon('map', 20) : icon('text', 20);
      const modeLabel = this.context.i18n.t(`tray.display.${dest}`);
      const switchTo = this.context.i18n.t('tray.switchTo').replace('{{mode}}', modeLabel);
      this.displayBtn.innerHTML = `<span class="tray-display-icon" aria-hidden="true">${glyph}</span><span class="tray-display-label">${switchTo}</span>`;
    }
  }
}
